import React, { useState, useRef, useEffect } from 'react'
import { useCollectionsStore } from '../../../Storage/useCollectionStore'
import songIcon from '../../icons/collections_icons/songIcon.png'
import artistIcon from '../../icons/collections_icons/artistIcon.png'
import mutedButton from '../../icons/collections_icons/mutedButton.png'
import unmutedButton from '../../icons/collections_icons/unmutedButton.png'
import PropTypes from 'prop-types'
import classes from './index.module.css'
import PublicationBadge from '../../GeneralComponents/PublicationBadge'
import { usePreventFullscreen } from '../../../Helpers/hooks/usePreventFullscreen'

export default function FeelItemCard({ feel, onClick, onDelete, onMoveToFront, onMoveToEnd, onNeedsSync, isDragging, count }) {
  const videoElement = useRef(null)
  const menuRef = useRef(null)
  const [shouldShowVideo, setShouldShowVideo] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { feelsMuted, setFeelsMuted } = useCollectionsStore()
  usePreventFullscreen(videoElement)

  const isRestrictedArtist =
    Boolean(feel?.artist?.is_restricted) ||
    (feel?.artists || []).some(artist => artist?.is_restricted)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false)
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const handleDetailsClicked = () => {
    if (onClick) {
      onClick(feel)
    }
  }

  const handleOnDelete = () => {
    if (onDelete) {
      onDelete(feel)
    }
  }

  const handleOnTimeUpdate = () => {
    if (videoElement.current.currentTime >= videoElement.current.duration) {
      videoElement.current.currentTime = 0
      videoElement.current.play()
    }
  }

  async function handleVideoPlay() {
    try {
      await videoElement.current.play()
    } catch (error) {
      // solution for error 'The play() request was interrupted by a call to pause()'
    }
  }

  function handleVideoPause() {
    if (!videoElement.current.paused) {
      videoElement.current.pause()
      videoElement.current.currentTime = 0
    }
  }

  return (
    <div
      className={classes.wrapper}
      style={{
        borderLeft: isDragging ? '1px dotted #d9d9d9' : 'none',
        borderBlock: isDragging ? '1px solid #8a8a8a' : 'none',
      }}
    >
      <div className={classes.scrollableContent}>
        <div className={classes.badgeHolder}><div className={classes.countBadge}>{count}</div></div>
        <div
          className={classes.feelImageContainer}
          onMouseOver={() => {
            setShouldShowVideo(true)
            if (videoElement.current) {
              handleVideoPlay()
            }
          }}
          onMouseLeave={() => {
            setShouldShowVideo(false)
            if (videoElement.current) {
              handleVideoPause()
            }
          }}
        >
          <img
            className={classes.feelImage}
            src={feel?.preview?.image?.image?.url}
            alt="image-preview"
          />
          {shouldShowVideo && (
            <video
              ref={videoElement}
              className={classes.feelVideo}
              controls={false}
              autoPlay={false}
              muted={feelsMuted}
              hidden={!shouldShowVideo}
              onTimeUpdate={() => {
                handleOnTimeUpdate()
              }}
              onClick={() => {
                handleDetailsClicked()
              }}
              src={feel?.preview?.video?.video?.url}
              controlsList="nofullscreen"
            />
          )}

          <img
            className={classes.playIcon}
            src={feelsMuted ? mutedButton : unmutedButton}
            hidden={!shouldShowVideo}
            onClick={() => {
              setFeelsMuted()
            }}
            alt="play-icon"
          />
        </div>

        {!feel?.is_public_visible && (
          <div className={classes.publishedBadgeContainer}>
            <PublicationBadge
              size="xs"
              status="unpublished"
              text="Unpublished"
            />
          </div>
        )}

        {isRestrictedArtist && (
          <div className={classes.restrictedBadgeContainer}>
            <span
              className={classes.restrictedTag}
              title="This artist has been restricted by their label or publisher."
            >Restricted</span>
          </div>
        )}

        <div
          className={classes.feelDetailsContainer}
          onClick={() => {
            handleDetailsClicked()
          }}
        >
          <span className={classes.lyricsTitle}>{feel.lyrics}</span>
          <div className={classes.itemInfoContainer}>
            <img className={classes.infoIcon} src={songIcon} alt="song-icon" />
            <span className={classes.itemInfoText}>{feel.song_name}</span>
          </div>
          <div className={classes.itemInfoContainer}>
            <img
              className={classes.infoIcon}
              src={artistIcon}
              alt="info-icon"
            />
            <span className={classes.itemInfoText}>{feel.artist.name}</span>
          </div>
        </div>
      </div>

      <div className={classes.actionsContainer}>
        <div className={classes.menuWrapper} ref={menuRef}>
          <div
            className={classes.menuTrigger}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            •••
          </div>
          {isMenuOpen && (
            <div className={classes.menuDropdown}>
              <div
                className={classes.menuItem}
                onClick={() => {
                  handleDetailsClicked()
                  setIsMenuOpen(false)
                }}
              >
                View Feels
              </div>
              {onMoveToFront && (
                <div
                  className={classes.menuItem}
                  onClick={() => {
                    onMoveToFront(feel)
                    setIsMenuOpen(false)
                  }}
                >
                  Move to Front
                </div>
              )}
              {onMoveToEnd && (
                <div
                  className={classes.menuItem}
                  onClick={() => {
                    onMoveToEnd(feel)
                    setIsMenuOpen(false)
                  }}
                >
                  Move to End
                </div>
              )}
              {onNeedsSync && (
                <div
                  className={classes.menuItem}
                  onClick={() => {
                    onNeedsSync(feel)
                    setIsMenuOpen(false)
                  }}
                >
                  Report Sync
                </div>
              )}
            </div>
          )}
        </div>
        <div className={classes.actionButton} onClick={() => handleOnDelete()}>
          <p className={classes.actionButtonTextRemove}>
            Remove from Collection
          </p>
        </div>
      </div>
    </div>
  )
}

FeelItemCard.propTypes = {
  feel: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  onMoveToFront: PropTypes.func,
  onMoveToEnd: PropTypes.func,
  onNeedsSync: PropTypes.func,
  isDragging: PropTypes.bool,
  count: PropTypes.number,
}
